import {Group} from '@visx/group';
import {Bar} from '@visx/shape';
import React from 'react';
import {useSelector} from 'react-redux';

import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {Sequence} from 'components/Sequence';
import CodonOptimizationTabState from 'molbio/codon-optimization/CodonOptimizationTabState';
import {getVerticalPlacement} from 'molbio/maps/linear/basesOverlayUtil';
import {timesPerSlice, whichDisplay} from 'molbio/maps/linear/LayeringHelpers';
import * as sequtil from 'molbio/SeqUtil';
import {BenchlingState} from 'redux-stores/reduceApp';

interface CodonOptimizationSelectionProps {
  sequence: Sequence;
  codonOptimizationTabId: string;
  sliceStart: number;
  sliceEnd: number;
  showingComplement: boolean;
  basesHeight: number;
  xFn: (index: number) => number;
}

function CodonOptimizationSelection({
  sequence,
  codonOptimizationTabId,
  sliceStart,
  sliceEnd,
  showingComplement,
  basesHeight,
  xFn,
}: CodonOptimizationSelectionProps): JSX.Element {
  const tabState = useSelector(
    (state: BenchlingState) => state.ui.codonOptimizationTab[codonOptimizationTabId]
  );
  if (tabState == null) {
    // The tab is in the process of being closed, so the Redux state has been cleared out but the
    // tab Backbone model is still present for now, or at least the remove event has not been triggered.
    return <></>;
  }

  const {optimizationStart, optimizationEnd, strand} = tabState;
  if (
    optimizationStart == null ||
    optimizationEnd == null ||
    strand == null ||
    (!showingComplement && strand === -1) ||
    !sequtil.isRangeOverlap(sliceStart, sliceEnd, optimizationStart, optimizationEnd)
  ) {
    return <></>;
  }

  // Possible that the selection wraps the slice.
  const selectionDisplayRegions = timesPerSlice(
    {},
    sliceStart,
    sliceEnd,
    optimizationStart,
    optimizationEnd
  ).map(
    (_, i) => whichDisplay(sliceStart, sliceEnd, optimizationStart, optimizationEnd, i, sequence.length())!
  );

  return (
    <Group>
      {selectionDisplayRegions.map(({start, end}) => (
        <Bar
          key={`${start}-${end}`}
          x={xFn(start)}
          width={xFn(end) - xFn(start)}
          fill="#D2E3FF"
          fillOpacity={0.4}
          stroke="black"
          strokeWidth={1}
          onMouseDown={() => {
            sequence.selection().setEndpoints(optimizationStart, optimizationEnd);
          }}
          onDoubleClick={() => {
            sequence.selection().setEndpoints(optimizationStart, optimizationEnd);
          }}
          onContextMenu={(e) => {
            sequence.selection().setEndpoints(optimizationStart, optimizationEnd);
            selectioncontextmenu.open(e.nativeEvent, sequence, selectioncontextmenu.getSelectionMenu());
          }}
          {...getVerticalPlacement(showingComplement, basesHeight, strand)}
        />
      ))}
    </Group>
  );
}

export default function CodonOptimizationSelectionWrapper(
  props: Omit<CodonOptimizationSelectionProps, 'codonOptimizationTabId'>
): JSX.Element {
  const codonOptimizationTabState = props.sequence
    .fileState()
    .findTab(CodonOptimizationTabState, (t: CodonOptimizationTabState) => t.get('active'));

  if (codonOptimizationTabState == null) {
    return <></>;
  } else {
    return <CodonOptimizationSelection {...props} codonOptimizationTabId={codonOptimizationTabState.cid} />;
  }
}
